import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiCalculator, FiAlertCircle } from 'react-icons/fi';
import { calculateFormula, extractFieldReferences, validateFormula } from '../utils/calculationEngine';

const CalculationContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CalculationInput = styled.input`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  background-color: #f9fafb;
  color: #374151;
  cursor: not-allowed;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    background-color: #ffffff;
  }

  &.error {
    border-color: #ef4444;
    background-color: #fef2f2;
    color: #dc2626;
  }

  &.success {
    border-color: #10b981;
    background-color: #f0fdf4;
    color: #059669;
  }
`;

const FormulaDisplay = styled.div`
  padding: 8px 12px;
  background-color: #f3f4f6;
  border-radius: 6px;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #6b7280;
  border: 1px solid #e5e7eb;
  min-height: 20px;
`;

const ErrorMessage = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: #dc2626;
  font-size: 14px;
  margin-top: 4px;
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-top: 4px;
  line-height: 1.4;
`;

const CalculationField = ({ 
  field, 
  value, 
  onChange, 
  formData, 
  showFormula = false,
  showHelp = false 
}) => {
  const [calculationResult, setCalculationResult] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [isValid, setIsValid] = useState(true);

  // Calculate result when form data or formula changes
  useEffect(() => {
    if (!field.formula) {
      setCalculationResult(null);
      setValidationError(null);
      setIsValid(true);
      return;
    }

    // Validate formula syntax
    const validation = validateFormula(field.formula);
    if (!validation.isValid) {
      setValidationError(validation.error);
      setIsValid(false);
      setCalculationResult(null);
      return;
    }

    setValidationError(null);
    setIsValid(true);

    // Calculate the result
    const result = calculateFormula(
      field.formula,
      formData,
      field.decimalPlaces || 2,
      field.currency || false
    );

    setCalculationResult(result);
    
    // Update the field value
    if (result.value !== value) {
      onChange(result.value);
    }
  }, [field.formula, field.decimalPlaces, field.currency, formData, value, onChange]);

  // Get field references for help text
  const fieldReferences = field.formula ? extractFieldReferences(field.formula) : [];

  return (
    <CalculationContainer>
      <CalculationInput
        type="text"
        value={calculationResult?.formattedValue || (field.currency ? '$0.00' : '0.00')}
        readOnly
        className={validationError ? 'error' : (calculationResult?.value !== 0 ? 'success' : '')}
        placeholder="Calculated value will appear here"
      />
      
      {showFormula && field.formula && (
        <FormulaDisplay>
          Formula: {field.formula}
        </FormulaDisplay>
      )}
      
      {validationError && (
        <ErrorMessage>
          <FiAlertCircle size={16} />
          {validationError}
        </ErrorMessage>
      )}
      
      {showHelp && field.formula && (
        <HelpText>
          <strong>Formula Help:</strong><br />
          {fieldReferences.length > 0 && (
            <>
              <strong>References:</strong> {fieldReferences.join(', ')}<br />
            </>
          )}
          <strong>Operators:</strong> +, -, *, /, ^, %<br />
          <strong>Functions:</strong> SUM(), AVERAGE(), MAX(), MIN(), ROUND(), IF()<br />
          <strong>Example:</strong> {fieldReferences[0] || 'field1'} + {fieldReferences[1] || 'field2'} * 0.1
        </HelpText>
      )}
      
      {showHelp && !field.formula && (
        <HelpText>
          <strong>Calculation Field Help:</strong><br />
          • Use field names in curly braces: {`{fieldName}`}<br />
          • Basic math: +, -, *, /, ^, %<br />
          • Functions: SUM(), AVERAGE(), MAX(), MIN(), ROUND(), IF()<br />
          • Example: {`{quantity} * {price} * 1.1`}
        </HelpText>
      )}
    </CalculationContainer>
  );
};

export default CalculationField;
